'use client';

import { useRef, useState } from 'react';
import { consumeToolUsage } from '@/lib/browser-tool-usage';
import { CATEGORY_COLORS, ELEMENTS, type Category, type Element } from '@/lib/periodic-table';

const CATEGORIES = Object.keys(CATEGORY_COLORS) as Category[];

function Detail({ e }: { e: Element }) {
  const rows: Array<[string, string]> = [
    ['Atomic number', String(e.z)],
    ['Atomic mass', `${e.mass} u`],
    ['Category', e.category + (e.predicted ? ' (predicted)' : '')],
    [
      'Group',
      e.group
        ? String(e.group)
        : e.category === 'lanthanide' || e.category === 'actinide'
          ? 'f-block (3)'
          : '—',
    ],
    ['Period', String(e.period)],
    ['Block', e.block],
    ['Electron configuration', e.config],
    [
      'State at 20 °C',
      e.state === 'unknown' ? 'Unknown (made only a few atoms at a time)' : e.state,
    ],
  ];
  return (
    <div
      style={{
        border: `3px solid ${CATEGORY_COLORS[e.category]}`,
        borderRadius: 12,
        padding: '0.75rem 1rem',
        display: 'grid',
        gap: '0.5rem',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.75rem' }}>
        <span style={{ fontSize: '2.6rem', fontWeight: 800, lineHeight: 1 }}>{e.symbol}</span>
        <span style={{ fontSize: '1.2rem', fontWeight: 600 }}>{e.name}</span>
      </div>
      <div className="tool-html-meta-grid">
        {rows.map(([k, v]) => (
          <div className="tool-live-field" key={k}>
            <span>{k}</span>
            <input readOnly type="text" value={v} />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function BrowserPeriodicTable({
  config,
}: {
  config?: { toolSlug?: string; heading?: string };
}) {
  const [selected, setSelected] = useState<number>(26);
  const [focus, setFocus] = useState<Category | null>(null);
  const [query, setQuery] = useState('');
  const reported = useRef(false);
  const touch = () => {
    if (reported.current) return;
    reported.current = true;
    void consumeToolUsage(config?.toolSlug ?? 'periodic-table', '').catch(() => undefined);
  };

  const q = query.trim().toLowerCase();
  const match = (e: Element) =>
    !q || e.symbol.toLowerCase() === q || e.name.toLowerCase().startsWith(q) || String(e.z) === q;

  const grid = (
    <div style={{ overflowX: 'auto', paddingBottom: 4 }}>
      <div
        role="grid"
        aria-label="Periodic table of the elements"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(18, minmax(2.4rem, 1fr))',
          gridTemplateRows: 'repeat(7, auto) 0.6rem repeat(2, auto)',
          gap: 2,
          minWidth: 720,
        }}
      >
        {ELEMENTS.map((e) => {
          const dim = (focus && e.category !== focus) || !match(e);
          return (
            <button
              aria-label={`${e.name}, ${e.z}`}
              aria-pressed={selected === e.z}
              key={e.z}
              onClick={() => {
                touch();
                setSelected(e.z);
              }}
              style={{
                gridRow: e.row,
                gridColumn: e.col,
                background: CATEGORY_COLORS[e.category],
                color: '#111',
                opacity: dim ? 0.18 : 1,
                border: selected === e.z ? '2px solid currentColor' : '1px solid rgba(0,0,0,0.15)',
                outline: selected === e.z ? '2px solid #111' : 'none',
                borderRadius: 4,
                padding: '0.15rem 0.1rem',
                textAlign: 'center',
                lineHeight: 1.05,
                cursor: 'pointer',
                minWidth: 0,
                fontStyle: e.predicted ? 'italic' : 'normal',
              }}
              type="button"
            >
              <div style={{ fontSize: '0.55rem' }}>{e.z}</div>
              <div style={{ fontWeight: 800, fontSize: '0.95rem' }}>{e.symbol}</div>
              <div
                style={{
                  fontSize: '0.45rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {e.name}
              </div>
            </button>
          );
        })}
        {[
          [6, '57-71'],
          [7, '89-103'],
        ].map(([row, label]) => (
          <div
            key={label}
            style={{
              gridRow: row as number,
              gridColumn: 3,
              fontSize: '0.6rem',
              display: 'grid',
              placeItems: 'center',
              border: '1px dashed rgba(127,127,127,0.6)',
              borderRadius: 4,
            }}
          >
            {label}
          </div>
        ))}
      </div>
    </div>
  );

  const legend = (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', margin: '0.5rem 0' }}>
      {CATEGORIES.map((c) => (
        <button
          aria-pressed={focus === c}
          key={c}
          onClick={() => setFocus((f) => (f === c ? null : c))}
          style={{
            background: CATEGORY_COLORS[c],
            color: '#111',
            border: focus === c ? '2px solid #111' : '1px solid transparent',
            borderRadius: 999,
            padding: '0.2rem 0.6rem',
            fontSize: '0.8rem',
            cursor: 'pointer',
          }}
          type="button"
        >
          {c}
        </button>
      ))}
    </div>
  );

  const current = ELEMENTS[selected - 1];

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Reference</span>
        <h2 className="tool-live-title">{config?.heading ?? 'Periodic table'}</h2>
        <p className="tool-live-subtitle">
          Click an element for its details. Tap a category to highlight it; search by name, symbol
          or number.
        </p>
      </div>
      <label className="tool-live-field" style={{ maxWidth: 320, marginBottom: '0.5rem' }}>
        <span>Find an element</span>
        <input
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Fe, iron or 26"
          type="text"
          value={query}
        />
      </label>
      {legend}
      {grid}
      {current ? (
        <div style={{ marginTop: '0.75rem' }}>
          <Detail e={current} />
        </div>
      ) : null}
      <p className="tool-live-panel-note">
        Atomic masses are IUPAC standard values; a number in brackets is the mass number of the
        longest-lived isotope of an element with no stable isotopes. Italic elements (109-118) have
        predicted, not measured, chemistry.
      </p>
    </div>
  );
}
